// /src/reports/DebtsReport.js
import React, { useState, useEffect } from "react";
import { supabase } from "../pages/supabaseClient";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";

export default function DebtsReport({ fromDate, toDate }) {
  const [creditors, setCreditors] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [filteredCreditors, setFilteredCreditors] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [creditorFilter, setCreditorFilter] = useState("");
  const [showLedger, setShowLedger] = useState(false);
  const [ledgerCreditor, setLedgerCreditor] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [creditors, transactions, statusFilter, creditorFilter, fromDate, toDate]);

  const fetchData = async () => {
    const { data: debts } = await supabase.from("debts").select("*").order("creditor");
    const { data: trans } = await supabase.from("debt_transactions").select("*").order("date", { ascending: true });
    setCreditors(debts || []);
    setTransactions(trans || []);
  };

  const applyFilters = () => {
    let filtered = creditors;

    if (statusFilter !== "All") {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }
    if (creditorFilter) {
      filtered = filtered.filter((c) => c.creditor === creditorFilter);
    }

    // Apply date range filter to transactions
    const transInRange = transactions.filter(
      (t) =>
        (!fromDate || t.date >= fromDate) &&
        (!toDate || t.date <= toDate)
    );

    // Calculate totals for each creditor using filtered transactions
    const filteredWithTotals = filtered.map((c) => {
      const creditorTrans = transInRange.filter((t) => t.debt_id === c.id);
      const totalLoaned = creditorTrans.filter((t) => t.type === "Loan").reduce((sum, t) => sum + t.amount, 0);
      const totalPaid = creditorTrans.filter((t) => t.type === "Payment").reduce((sum, t) => sum + t.amount, 0);
      const balance = totalLoaned - totalPaid;
      return { ...c, totalLoaned, totalPaid, balance };
    });

    setFilteredCreditors(filteredWithTotals);
  };

  const openLedger = (creditor) => {
    setLedgerCreditor(creditor);
    const ledgerRows = transactions
      .filter((t) => t.debt_id === creditor.id)
      .filter((t) => (!fromDate || t.date >= fromDate) && (!toDate || t.date <= toDate))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    setLedgerData(ledgerRows);
    setShowLedger(true);
  };

  const exportSummaryCSV = () => {
    const headers = ["Creditor", "Total Loaned", "Total Paid", "Balance", "Status"];
    const rows = filteredCreditors.map((c) => [
      c.creditor,
      c.totalLoaned.toFixed(2),
      c.totalPaid.toFixed(2),
      c.balance.toFixed(2),
      c.status
    ]);
    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    saveAs(blob, `debts_summary_${format(new Date(), "yyyyMMdd_HHmm")}.csv`);
  };

  const exportLedgerCSV = () => {
    const headers = ["Date", "Type", "Amount", "Notes", "Running Balance", "Logged By", "Logged At"];
    let running = 0;
    const rows = ledgerData.map((r) => {
      running += r.type === "Loan" ? r.amount : -r.amount;
      return [
        r.date,
        r.type,
        r.amount.toFixed(2),
        r.notes || "",
        running.toFixed(2),
        r.logged_by || "",
        r.logged_at ? format(new Date(r.logged_at), "dd/MM/yyyy HH:mm") : ""
      ];
    });
    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    saveAs(blob, `ledger_${ledgerCreditor.creditor}_${format(new Date(), "yyyyMMdd_HHmm")}.csv`);
  };

  const grandTotals = filteredCreditors.reduce(
    (acc, c) => {
      acc.loaned += c.totalLoaned || 0;
      acc.paid += c.totalPaid || 0;
      acc.balance += c.balance || 0;
      return acc;
    },
    { loaned: 0, paid: 0, balance: 0 }
  );

  return (
    <div>
      <h3>Debts Management</h3>

      {/* Filters */}
      <fieldset style={{ marginBottom: "20px" }}>
        <legend>Filters</legend>
        <label>Status: </label>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option>All</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>

        <label style={{ marginLeft: "10px" }}>Creditor: </label>
        <select value={creditorFilter} onChange={(e) => setCreditorFilter(e.target.value)}>
          <option value="">All</option>
          {creditors.map((c) => (
            <option key={c.id} value={c.creditor}>{c.creditor}</option>
          ))}
        </select>

        <label style={{ marginLeft: "10px" }}>From: </label>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />

        <label style={{ marginLeft: "10px" }}>To: </label>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />

        <button onClick={applyFilters} style={{ marginLeft: "10px" }}>Apply</button>
        <button onClick={exportSummaryCSV} style={{ marginLeft: "10px" }}>Export Summary CSV</button>
      </fieldset>

      {/* Creditors Table */}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#eee" }}>
            <th>Creditor</th>
            <th>Total Loaned</th>
            <th>Total Paid</th>
            <th>Balance</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredCreditors.map((c) => (
            <tr key={c.id} style={{ cursor: "pointer" }}>
              <td>{c.creditor}</td>
              <td>£{c.totalLoaned.toFixed(2)}</td>
              <td>£{c.totalPaid.toFixed(2)}</td>
              <td>£{c.balance.toFixed(2)}</td>
              <td>{c.status}</td>
              <td>
                <button onClick={() => openLedger(c)}>View Ledger</button>
              </td>
            </tr>
          ))}
          <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
            <td>Grand Total</td>
            <td>£{grandTotals.loaned.toFixed(2)}</td>
            <td>£{grandTotals.paid.toFixed(2)}</td>
            <td>£{grandTotals.balance.toFixed(2)}</td>
            <td colSpan={2}></td>
          </tr>
        </tbody>
      </table>

      {/* Ledger Modal */}
      {showLedger && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          justifyContent: "center", alignItems: "flex-start", padding: "20px",
          zIndex: 1000
        }}>
          <div style={{ background: "white", padding: "20px", borderRadius: "8px", maxWidth: "800px", width: "100%", maxHeight: "80vh", overflowY: "auto" }}>
            <h3>Ledger - {ledgerCreditor.creditor}</h3>
            <button onClick={exportLedgerCSV} style={{ marginBottom: "10px" }}>Export Ledger CSV</button>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#eee" }}>
                  <th>Date</th><th>Type</th><th>Amount</th><th>Notes</th><th>Running Balance</th><th>Logged By</th><th>Logged At</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let running = 0;
                  return ledgerData.map((r, i) => {
                    running += r.type === "Loan" ? r.amount : -r.amount;
                    return (
                      <tr key={i} style={{ background: r.notes?.toLowerCase().startsWith("correction:") ? "#fff8dc" : "white" }}>
                        <td>{r.date}</td>
                        <td>{r.type}</td>
                        <td>£{r.amount.toFixed(2)}</td>
                        <td>{r.notes}</td>
                        <td style={{ color: r.type === "Loan" ? "crimson" : "seagreen" }}>£{running.toFixed(2)}</td>
                        <td>{r.logged_by}</td>
                        <td>{r.logged_at ? format(new Date(r.logged_at), "dd/MM/yyyy HH:mm") : ""}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
            <button onClick={() => setShowLedger(false)} style={{ marginTop: "10px" }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
